import { IconChevronLeft } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import type { ReactNode } from "react";

interface PageHeaderProps {
  /** Mono uppercase section eyebrow above the title, e.g. `Fleet`. */
  eyebrow?: string;
  title: string;
  subtitle?: string;
  /** Optional "back to…" link shown above the title. */
  back?: { to: string; label: string };
  /** Right-aligned actions. */
  actions?: ReactNode;
}

/**
 * The header of a screen: where you are, and what you can do here.
 *
 * Args:
 *     eyebrow: Section label in the house mono style.
 *     title: Screen title, rendered as the page's `h1`.
 *     subtitle: One line of context.
 *     back: Optional back link.
 *     actions: Optional right-aligned actions.
 *
 * Returns:
 *     The page header.
 */
export function PageHeader({
  eyebrow,
  title,
  subtitle,
  back,
  actions,
}: PageHeaderProps): JSX.Element {
  return (
    <header className="page-head">
      <div className="page-head__text">
        {back ? (
          <Link className="back-link" to={back.to}>
            <IconChevronLeft size={13} aria-hidden="true" />
            {back.label}
          </Link>
        ) : null}
        {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
        <h1 className="page-head__title">{title}</h1>
        {subtitle ? <p className="page-head__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="page-head__actions">{actions}</div> : null}
    </header>
  );
}
